import React, { useState } from 'react'
import { Drawer } from 'antd'
import { useHistory, useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'

import { normalize, notifOps, summarizeProcurementInvoice, usePet, useToggle } from '../../modules'

import Receive from '../reusable/receive'
import Confirm from '../reusable/confirm'

// Applies an already-recorded, unused credit note against one or more outstanding
// invoices - the counterpart to recordCreditNote's "apply at creation time" path, for
// credits recorded without picking an invoice up front (the common case: you don't
// always know yet which invoice a credit will offset). The backend allocates
// sequentially across whichever invoices are selected here, capping each at its own
// outstanding balance - this drawer only picks which invoices are in play.
const ApplyCreditNote = ({ onPerform }) => {
    const { noteId, supplierId } = useParams()
    const [selected, setSelected] = useState([])

    const { enqueueSnackbar } = useSnackbar()
    const [visible, toggleVisible] = useToggle(true)
    const history = useHistory()

    const [{ loading: noteLoading, error: noteError, data: creditNote }] = usePet('creditNote', { id: noteId }, { notify: true })
    const [{ loading: invLoading, error: invError, data: invoicesData }] = usePet('invoices', { supplier: supplierId, limit: 200 }, { notify: true })

    const outstandingInvoices = (invoicesData?.invoices ?? []).filter(inv => !inv.paid && inv.committed)
    const unused = Number(creditNote?.amount ?? 0) - Number(creditNote?.amountApplied ?? 0)

    const toggleInvoice = inv => setSelected(sel => sel.some(i => i._id === inv._id) ? sel.filter(i => i._id !== inv._id) : [...sel, inv])

    const close = () => {
        toggleVisible()
        setTimeout(() => history.goBack(), 300)
    }

    const onComplete = (_, notif, ops) => {
        onPerform()
        notif('Credit note applied', ops('success'))
        setTimeout(close, 300)
    }

    const [{ loading }, execute] = usePet('applyCreditNote', {
        creditNote: noteId,
        invoiceIds: selected.map(inv => inv._id),
    }, { notify: true, callback: onComplete, manual: true })

    const apply = () => {
        if (!selected.length) {
            enqueueSnackbar('Select at least one invoice to apply this credit against', notifOps('warning'))
            return
        }

        Confirm({
            title: 'Apply credit note?',
            message: `This will apply up to Ksh. ${normalize(unused)} of unused credit against the selected invoice(s).`,
            okText: 'Apply Credit',
            onOk: execute,
        })
    }

    return (
        <Drawer
            closable
            maskClosable
            visible={visible}
            onClose={close}
            footer={
                <div className="w-full text-right">
                    <button disabled={ loading || !(unused > 0) } onClick={apply} className="text-white mr-2 hisani-btn-orange font-medium rounded-lg text-sm px-5 py-2.5 text-center">Apply</button>
                </div>
            }
            width="40%">
            <p className="font-extrabold text-3xl mb-0">Apply Credit Note</p>
            <Receive error={noteError} loading={noteLoading} progress>
                <p className="text-sm italic mb-1 text-gray-700">Unused balance: <span className="font-semibold">Ksh. { normalize(unused) }</span></p>
                <p className="text-sm mb-3 text-gray-700">{ creditNote?.reason }</p>
            </Receive>

            <div className="w-full mb-3">
                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">Select invoices to apply against</label>
                <Receive error={invError} loading={invLoading} progress>
                    {
                        outstandingInvoices.length > 0 ?
                            <table className="table-auto w-full">
                                <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                    <tr>
                                        <th className="p-2"></th>
                                        <th className="p-2"><div className="font-semibold text-left">Code</div></th>
                                        <th className="p-2"><div className="font-semibold text-center">Outstanding</div></th>
                                    </tr>
                                </thead>
                                <tbody className="text-sm font-medium divide-y divide-gray-100">
                                    {
                                        outstandingInvoices.map(inv => {
                                            const outstanding = summarizeProcurementInvoice(inv.items, inv.amountPaid, inv.amountCredited).outstanding
                                            return (
                                                <tr key={inv._id}>
                                                    <td className="p-2">
                                                        <input
                                                            type="checkbox"
                                                            className="form-checkbox cursor-pointer h-5 w-5"
                                                            checked={ selected.some(i => i._id === inv._id) }
                                                            onChange={ () => toggleInvoice(inv) } />
                                                    </td>
                                                    <td className="p-2">{ inv.code }</td>
                                                    <td className="p-2"><div className="text-center">Ksh. { normalize(outstanding) }</div></td>
                                                </tr>
                                            )
                                        })
                                    }
                                </tbody>
                            </table>
                            :
                            <div className="w-full h-16 flex items-center justify-center">
                                <p className="font-semibold text-base">No outstanding invoices for this supplier</p>
                            </div>
                    }
                </Receive>
            </div>
        </Drawer>
    )
}

export default ApplyCreditNote
